import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";

import { useHubspot } from "../../../../contexts";
import { getMappingProgress } from "./mappingUtils";
import { getRoleStatus, useVisibleRoles } from "./pipelineRoles";
import { REVENUE_TYPES } from "./HsPipelinesStep";

const ChecklistRow = ({ ok, label, detail }) => (
  <div className="flex items-start gap-3 px-4 py-3">
    {ok ? (
      <HiCheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
    ) : (
      <HiExclamationCircle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" />
    )}
    <div>
      <p className="text-sm font-medium text-black">{label}</p>
      <p className="text-xs text-gray-500">{detail}</p>
    </div>
  </div>
);

// Step 3 of the setup: a readable checklist of everything configured so far,
// plus what enabling sync means. The enable button itself sits in the stepper
// footer.
export const HsReviewStep = () => {
  const { hubspotCustomizations, panoraFields, localMapping } = useHubspot();
  const visibleRoles = useVisibleRoles();

  const revenueType = REVENUE_TYPES.find(
    ({ value }) => value === hubspotCustomizations?.deal_revenue_type,
  );
  const { mapped, total } = getMappingProgress(panoraFields, localMapping);
  const allMapped = total > 0 && mapped === total;

  return (
    <div className="flex flex-col gap-4">
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
        <ChecklistRow
          ok={!!revenueType}
          label="Deal Revenue Type"
          detail={
            revenueType
              ? `Deal amounts are per ${revenueType.label.toLowerCase()}`
              : "Not set — choose it in the Pipelines step"
          }
        />
        {visibleRoles.map((role) => {
          const { configured, summary } = getRoleStatus(
            role,
            hubspotCustomizations,
          );
          return (
            <ChecklistRow
              key={role.key}
              ok={configured}
              label={role.title}
              detail={summary}
            />
          );
        })}
        <ChecklistRow
          ok={allMapped}
          label="Field mappings"
          detail={
            allMapped
              ? `All ${total} fields mapped`
              : `${mapped} of ${total} fields mapped — map the rest or mark them “Not used” in the Field mapping step`
          }
        />
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        Enabling sync locks this configuration and starts syncing HubSpot data
        into Panora. To change it later you'll need to contact Panora support,
        or disconnect and reconnect HubSpot.
      </div>
    </div>
  );
};
